import { useEffect } from "react";

const BASE_URL = "https://excerptus.io";

type Props = {
  name: string;
  description: string;
  path: string;
  /** Optional ISO date for `dateModified`. */
  dateModified?: string;
};

/**
 * Injects a WebPage JSON-LD block into <head> for the lifetime of the
 * mounting route. Uses a stable id so re-mounts swap rather than stack.
 */
export const WebPageJsonLd = ({ name, description, path, dateModified }: Props) => {
  useEffect(() => {
    const canonicalPath = path === "/" ? "/" : path.endsWith("/") ? path : `${path}/`;
    const url = canonicalPath === "/" ? BASE_URL : `${BASE_URL}${canonicalPath}`;
    const id = `jsonld-webpage-${canonicalPath}`;
    const payload = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name,
      description,
      url,
      isPartOf: { "@type": "WebSite", name: "Excerptus", url: BASE_URL },
      ...(dateModified ? { dateModified } : {}),
    };

    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(payload);

    return () => {
      el?.parentNode?.removeChild(el);
    };
  }, [name, description, path, dateModified]);

  return null;
};
